import { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/auth/check-auth');
      setUser(response.data.user);
    } catch (error) {
      console.error('Check auth error:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, first_name, last_name, password) => {
    try {
      // Create form data
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('first_name', first_name);
      formData.append('last_name', last_name);
      formData.append('password', password);

      // Get user's IP address
      let userIP = 'unknown';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        userIP = ipData.ip;
      } catch (ipError) {
        console.warn('Failed to fetch IP address:', ipError);
        // Continue with 'unknown' IP - backend will handle this appropriately
      }

      // First get the token
      const response = await axios.post('/auth/login', formData, {
        params: {
          ip: userIP,
        },
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      if (response.data.access_token) {
        // Save token
        localStorage.setItem('token', response.data.access_token);
        
        // Get user data with the new token
        const userResponse = await axios.get('/auth/check-auth');
        const userData = userResponse.data.user;
        
        // Update context
        setUser(userData);
        
        // Return both token and user data
        return {
          ...response.data,
          user: userData
        };
      }
      throw new Error('No access token received');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle IP-related errors with more specific messaging
      const errorMessage = error.response?.data?.detail || 'Login failed';
      if (errorMessage.includes('IP address') || errorMessage.includes('not authorized')) {
        throw new Error(errorMessage);
      }
      
      throw new Error(errorMessage);
    }
  };

  const signup = async (username, first_name, last_name, password) => {
    try {
      const response = await axios.post('/student/signup', {
        username,
        password,
      });
      return response.data;
    } catch (error) {
      console.error('Signup error:', error);
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    signup,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
